import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";

const getBudgetRef = (uid, workspaceId) =>
  workspaceId
    ? doc(db, "workspaces", workspaceId, "budget", "data")
    : doc(db, "users", uid, "budget", "data");

function computeHealth({ runwaySettings, transactions, categories }) {
  const now = new Date();
  const thisKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const lastKey = (() => { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })();
  const thisTxs = transactions.filter(tx => tx.date?.startsWith(thisKey));
  const lastTxs = transactions.filter(tx => tx.date?.startsWith(lastKey));
  const thisBurn = thisTxs.reduce((s, tx) => s + Number(tx.amount), 0);
  const lastBurn = lastTxs.reduce((s, tx) => s + Number(tx.amount), 0);
  const mrr  = Number(runwaySettings?.mrr) || 0;
  const cash = Number(runwaySettings?.cashBalance) || 0;
  const burn = Number(runwaySettings?.monthlyBurnOverride) || thisBurn;
  const netBurn = Math.max(burn - mrr, 0);
  const runway = netBurn > 0 && cash > 0 ? Math.floor(cash / netBurn) : null;
  let score = 0, max = 0;
  if (runway !== null) { max += 40; score += runway >= 18 ? 40 : runway >= 12 ? 32 : runway >= 9 ? 24 : runway >= 6 ? 16 : runway >= 3 ? 8 : 0; }
  const limCats = categories.filter(c => c.limit > 0);
  if (limCats.length > 0) {
    max += 30;
    const over = limCats.filter(c => thisTxs.filter(tx => tx.categoryId === c.id).reduce((s, tx) => s + Number(tx.amount), 0) > c.limit);
    score += Math.round((1 - over.length / limCats.length) * 30);
  }
  if (lastBurn > 0 && thisBurn > 0) { max += 20; const chg = (thisBurn - lastBurn) / lastBurn; score += chg <= 0 ? 20 : chg <= 0.1 ? 14 : chg <= 0.25 ? 8 : 0; }
  if (burn > 0) { max += 10; const cov = mrr / burn; score += cov >= 0.5 ? 10 : cov >= 0.25 ? 6 : cov >= 0.1 ? 3 : 0; }
  if (max === 0) return null;
  return Math.round((score / max) * 100);
}

export default function ShareDashboard() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | notfound | disabled | ready
  const [data, setData] = useState(null);

  useEffect(() => {
    let unsub;
    (async () => {
      const shareSnap = await getDoc(doc(db, "shares", token));
      if (!shareSnap.exists()) { setStatus("notfound"); return; }
      const { uid, workspaceId } = shareSnap.data();
      const ref = getBudgetRef(uid, workspaceId);
      unsub = onSnapshot(ref, snap => {
        if (!snap.exists() || !snap.data().shareEnabled) { setStatus("disabled"); return; }
        setData(snap.data());
        setStatus("ready");
      });
    })();
    return () => unsub?.();
  }, [token]);

  const s = {
    page: { minHeight: "100vh", background: "#000", color: "#f9fafb", fontFamily: "'Inter', system-ui, sans-serif", padding: "32px 24px", maxWidth: 720, margin: "0 auto" },
    card: { background: "#0a0a0a", border: "1px solid #111", borderRadius: 8, padding: "14px 16px", marginBottom: 8 },
    label: { fontSize: 9, color: "#888", fontFamily: "'DM Mono', monospace", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 },
    mono: { fontFamily: "'DM Mono', monospace" },
    value: { fontSize: 20, fontWeight: 500, fontFamily: "'DM Mono', monospace" },
  };

  if (status === "loading") return (
    <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#22c55e" }} />
    </div>
  );

  if (status === "notfound" || status === "disabled") return (
    <div style={{ ...s.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
      <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#555" }} />
      <span style={{ ...s.mono, fontSize: 11, color: "#555" }}>this dashboard is not available</span>
      <a href="/" style={{ ...s.mono, fontSize: 10, color: "#333", textDecoration: "none" }}>greenline.app</a>
    </div>
  );

  const now = new Date();
  const thisKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const transactions   = data.transactions ?? [];
  const categories     = data.categories ?? [];
  const runwaySettings = data.runwaySettings ?? {};
  const monthlyBudget  = data.monthlyBudget ?? 0;

  const thisTxs   = transactions.filter(tx => tx.date?.startsWith(thisKey));
  const monthSpent = thisTxs.reduce((s, tx) => s + Number(tx.amount), 0);
  const mrr        = Number(runwaySettings.mrr) || 0;
  const cash       = Number(runwaySettings.cashBalance) || 0;
  const burn       = Number(runwaySettings.monthlyBurnOverride) || monthSpent;
  const netBurn    = Math.max(burn - mrr, 0);
  const runway     = netBurn > 0 && cash > 0 ? Math.floor(cash / netBurn) : null;
  const runwayColor = runway == null ? "#555" : runway <= 3 ? "#ef4444" : runway <= 6 ? "#f59e0b" : "#22c55e";
  const healthScore = computeHealth({ runwaySettings, transactions, categories });
  const healthColor = healthScore == null ? "#555" : healthScore >= 70 ? "#22c55e" : healthScore >= 40 ? "#f59e0b" : "#ef4444";

  const months6 = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { label: d.toLocaleDateString("en-US", { month: "short" }).toLowerCase(), total: transactions.filter(tx => tx.date?.startsWith(key)).reduce((s, tx) => s + Number(tx.amount), 0), isCurrent: i === 5 };
  });
  const maxBar = Math.max(...months6.map(m => m.total), 1);

  const monthLabel = now.toLocaleDateString("en-US", { month: "long", year: "numeric" }).toLowerCase();

  return (
    <div style={s.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');`}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: 1, background: "#22c55e" }} />
            <span style={{ ...s.mono, fontSize: 12, color: "#22c55e", fontWeight: 600 }}>Greenline</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 500, color: "#f9fafb" }}>Financial Dashboard</div>
          <div style={{ ...s.mono, fontSize: 10, color: "#555", marginTop: 2 }}>$ {monthLabel}</div>
        </div>
        <span style={{ ...s.mono, fontSize: 9, color: "#333", background: "#0a0a0a", border: "1px solid #111", borderRadius: 4, padding: "3px 8px" }}>read-only</span>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8, marginBottom: 8 }}>
        {[
          { label: "MONTHLY_BURN",  value: burn > 0 ? `$${burn.toLocaleString()}` : "—" },
          { label: "MRR",           value: mrr > 0  ? `$${mrr.toLocaleString()}`  : "—" },
          { label: "CASH_BALANCE",  value: cash > 0 ? `$${cash.toLocaleString()}` : "—" },
          { label: "RUNWAY",        value: runway != null ? `${runway}mo` : "—", color: runwayColor },
        ].map(({ label, value, color }) => (
          <div key={label} style={s.card}>
            <div style={s.label}>{label}</div>
            <div style={{ ...s.value, color: color || "#f9fafb" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Health score */}
      {healthScore != null && (
        <div style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={s.label}>FINANCIAL_HEALTH</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 4 }}>
              <span style={{ ...s.mono, fontSize: 28, fontWeight: 500, color: healthColor }}>{healthScore}</span>
              <span style={{ ...s.mono, fontSize: 10, color: "#555" }}>/100</span>
            </div>
          </div>
          <span style={{ ...s.mono, fontSize: 10, background: "transparent", border: `1px solid ${healthColor}44`, color: healthColor, borderRadius: 4, padding: "2px 8px" }}>
            {healthScore >= 70 ? "healthy" : healthScore >= 40 ? "watch closely" : "critical"}
          </span>
        </div>
      )}

      {/* Spending history */}
      <div style={{ ...s.card, marginTop: 8 }}>
        <div style={s.label}>SPENDING_HISTORY — last 6 months</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 80, marginTop: 12 }}>
          {months6.map(({ label, total, isCurrent }) => (
            <div key={label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span style={{ ...s.mono, fontSize: 8, color: isCurrent && total > 0 ? "#22c55e" : "transparent" }}>
                ${total >= 1000 ? (total / 1000).toFixed(1) + "k" : total}
              </span>
              <div style={{ width: "100%", background: "#111", borderRadius: 3, overflow: "hidden", height: 52, display: "flex", alignItems: "flex-end" }}>
                <div style={{ width: "100%", height: `${Math.max((total / maxBar) * 100, total > 0 ? 3 : 0)}%`, background: isCurrent ? "#22c55e" : "#1a1a1a", borderRadius: "2px 2px 0 0", transition: "height 0.4s ease" }} />
              </div>
              <span style={{ ...s.mono, fontSize: 8, color: isCurrent ? "#22c55e" : "#333" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Category breakdown */}
      {categories.length > 0 && (
        <div style={{ ...s.card, marginTop: 8 }}>
          <div style={{ ...s.label, marginBottom: 12 }}>CATEGORY_BREAKDOWN</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {categories.map(cat => {
              const spent = thisTxs.filter(tx => tx.categoryId === cat.id).reduce((s, tx) => s + Number(tx.amount), 0);
              const pct   = cat.limit > 0 ? Math.min((spent / cat.limit) * 100, 100) : 0;
              const fill  = pct >= 100 ? "#ef4444" : pct >= 75 ? "#f59e0b" : "#22c55e";
              return (
                <div key={cat.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ ...s.mono, fontSize: 11, color: "#777" }}>{cat.name}</span>
                    <span style={{ ...s.mono, fontSize: 10, color: "#666" }}>${spent.toLocaleString()}{cat.limit > 0 ? ` / $${cat.limit.toLocaleString()}` : ""}</span>
                  </div>
                  {cat.limit > 0 && (
                    <div style={{ height: 2, background: "#111", borderRadius: 1, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: fill, borderRadius: 1, transition: "width 0.5s ease" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ ...s.mono, fontSize: 9, color: "#222" }}>live data · updates automatically</span>
        <a href="/" style={{ ...s.mono, fontSize: 9, color: "#333", textDecoration: "none" }}>powered by greenline</a>
      </div>
    </div>
  );
}
